/**
 * The wing Reach subsystem (M6 spec §6.2; plan P25). Discovered from `src/<dir>/subsystem.ts` (M3); order 455, after
 * traffic (450), whose real ships it recruits.
 *
 * - **Overlay.** Installs the wing's commanded-state overlay on M1's pilots (`setPilotOverlay`, `commands.ts`).
 * - **Leader.** The player (`sim.playerEid`); the flown class comes from the flown ship's def id through hulls.json
 *   (`shipDef` → `class`) and the cap from that hull's stamped `wingSize` (M10 P2d2; none: `wing.size`), so the
 *   corvette's `wingSize: 5` lifts the cap to 5 and its class adds its +10%. Members over a lowered cap are released.
 *   The def id is the Career's flown ship (`CareerPort.flownShipDefId`, D-459 bug 1): M6 P24b flies a boarded ship on
 *   the start ship's def slot and keeps the slot's id, so the slot's id stands only without a career (the stand-in).
 * - **Recruit.** Own traffic ships (`store.own`, P22) whose fleet block says Escort(the player) (`escortPlayer`), real
 *   now (`store.eid`), not sent home: each gets its captain's Combat profile (`combatProfileId`) and its escort post
 *   as the Dock/return home, with the radius of the layout station there (`homeRadiusAt`, scale SC-5e2; 0 for a relay
 *   or open space), so Dock/return ends `dockArriveM` off a station's hull.
 * - **Pin.** Each tick a member's `store.combatAt` is refreshed: the traffic pilot leaves a ship with an `AIPilot` alone
 *   while it is in combat, and the tier step never demotes it for distance (`ai/traffic.ts`, `traffic/realShips.ts`;
 *   no edit to either).
 * - **Leave.** A member the traffic layer demoted or removed leaves the roster (only forgotten: its entity id may
 *   already be another ship's); one whose fleet block no longer says Escort(the player) goes back to the traffic pilot.
 * - **Dock/return.** A member that reached home is released to the traffic pilot (its post) and not recruited again
 *   until the next command recalls the wing.
 * - **Rendezvous.** The strategic escort parks an Escort(the player) ship at its post in the player's system (it does not
 *   know where the player is), so while the wing has room each such contact (not docked, not sent home) is placed at
 *   the player's formation slots, where the tier step promotes it and the wing recruits it (D-418). One docked there
 *   (a ship granted or parked at the player's dock) is brought out `launchS` (wing.json) after the player appears in
 *   space instead of waiting out its strategic docked step (D-459 bug 8); its step keeps its clock as a hold.
 * - **Steps.** A wingman's strategic leg (the core's next step after a placed dock or hold) becomes a hold on the leg's
 *   clock: the wing flies the ship, so it would never arrive and report its leg.
 * - **Relay follow.** Members within `followRelayM` of the player (the last tick before the system is left) are
 *   remembered; in the next system, once the core lists them, their contacts are placed at the player's formation
 *   slots so the tier step promotes them beside the player and the wing recruits them again (`wingRelayFollowers`).
 *
 * The UI (the radial menu, P31) calls `commandWing`; tests drive `wing.ts` / `commands.ts` directly. The per-tick step
 * allocates nothing (D-014); recruiting, releasing and the relay follow are rare.
 */
import { hasComponent } from 'bitecs'
import hullsJson from '../data/strategic/hulls.json'
import localJson from '../data/strategic/local.json'
import { beginTrafficPilot, type TrafficPilotEnv } from '../ai/traffic'
import { setPilotOverlay } from '../ai/pilot'
import type { StationSite } from '../galaxy/layout/types'
import { siteRadius } from '../reach/session/siteRadius'
import type { ReachSubsystem } from '../reach/runtime/types'
import type { Sim } from '../sim'
import { localConfigSchema } from '../strategic/schemas'
import type { ShipClassName } from '../strategic/types'
import { Agent } from '../traffic/components'
import { PURPOSE, STEP_KIND, type ContactStore } from '../traffic/contacts'
import { activeTrafficStore } from '../traffic/subsystem'
import { AIPilot, Life, Pos64, Ship } from '../world/components'
import { createWingOverlay, issueWingCommand, updateWing } from './commands'
import { writeSlotWorld, type FormationName } from './formation'
import {
  combatProfileId,
  createWing,
  dropMember,
  isWingman,
  recruitWingman,
  releaseWingman,
  setLeader,
  type Wing,
  type WingCommandName,
} from './wing'

/** Ship def id → fleet class (the first player-buyable hull on that def). */
const CLASS_BY_SHIP_DEF = new Map<string, ShipClassName>()
/** Ship def id → that same hull's stamped `wingSize` (M10 P2d2; absent: the hull has none, so `wing.size` applies). */
const WING_SIZE_BY_SHIP_DEF = new Map<string, number>()
for (const h of hullsJson.hulls as readonly { shipDef: string; class?: string; wingSize?: number }[]) {
  if (h.class === undefined || CLASS_BY_SHIP_DEF.has(h.shipDef)) continue
  CLASS_BY_SHIP_DEF.set(h.shipDef, h.class as ShipClassName)
  if (h.wingSize !== undefined) WING_SIZE_BY_SHIP_DEF.set(h.shipDef, h.wingSize)
}

/** The fleet class of ship def `id`, null when no player hull flies it. */
export function classOfShipDef(id: string): ShipClassName | null {
  return CLASS_BY_SHIP_DEF.get(id) ?? null
}

/** The stamped `wingSize` of the player hull flying ship def `id` (M10 P2d2), null when it has none or no hull flies it. */
export function wingSizeOfShipDef(id: string): number | null {
  return WING_SIZE_BY_SHIP_DEF.get(id) ?? null
}

export interface WingRuntime {
  readonly wing: Wing
  /** The sim the overlay was installed for. */
  sim: Sim | null
  /** M1 profile index by id (filled from the sim). */
  readonly profiles: Map<string, number>
  /** The leader's def index last seen (the class is looked up when it changes). */
  leaderDef: number
  /** The flown ship's def id last seen (`CareerPort.flownShipDefId`; null without a career). */
  leaderDefId: string | null
  /** Sim time the current leader appeared (launch, arrival, respawn); docked escorts come out `launchS` later. */
  leaderAt: number
  /** Fleet ship ids sent home (Dock/return) and not recalled yet. */
  readonly returned: Set<string>
  /** Fleet ship ids that follow the player through the relay just taken. */
  readonly followers: Set<string>
  /** Per member: 1 when within `followRelayM` of the player on the last tick. */
  readonly near: Uint8Array
  /** A traffic-pilot env for handing a member back (beginTrafficPilot reads only the store and the config). */
  pilotEnv: TrafficPilotEnv | null
  /** Scratch for the relay follow placement. */
  readonly place: Float64Array
}

export function createWingRuntime(wing: Wing = createWing()): WingRuntime {
  return {
    wing,
    sim: null,
    profiles: new Map(),
    leaderDef: -1,
    leaderDefId: null,
    leaderAt: 0,
    returned: new Set(),
    followers: new Set(),
    near: new Uint8Array(wing.max),
    pilotEnv: null,
    place: new Float64Array(wing.max * 3),
  }
}

const LOCAL_CFG = localConfigSchema.parse(localJson)

/** How close (metres) a home point must be to a layout station's centre to be that station (a strategic node sits on it). */
const HOME_SITE_MATCH_M = 1

/** The layout stations `homeRadiusAt` reads. */
export type HomeSites = readonly Pick<StationSite, 'position' | 'type' | 'assetId' | 'anchor'>[]

/**
 * The gameplay radius R (metres) of the layout station centred at (x, y, z) (`siteRadius`, the model the runtime
 * shows, D-912), or 0 when none is there (a relay post, open space, no layout). Scale SC-5e2. Rare (on recruit).
 */
export function homeRadiusAt(sites: HomeSites | null, x: number, y: number, z: number): number {
  if (sites === null) return 0
  const m2 = HOME_SITE_MATCH_M * HOME_SITE_MATCH_M
  for (let i = 0; i < sites.length; i++) {
    const p = sites[i].position
    const dx = p[0] - x
    const dy = p[1] - y
    const dz = p[2] - z
    if (dx * dx + dy * dy + dz * dz <= m2) return siteRadius(sites[i])
  }
  return 0
}

function pilotEnvFor(rt: WingRuntime, store: ContactStore): TrafficPilotEnv {
  if (rt.pilotEnv === null || rt.pilotEnv.store !== store) {
    rt.pilotEnv = { store, cfg: LOCAL_CFG, docking: null, bays: null, profileIndex: () => -1, arrived: () => undefined }
  }
  return rt.pilotEnv
}

/** The contact slot member `i` flies for now (the store compacts on removal; `Agent.index` follows), −1 if none. */
function slotOf(store: ContactStore | null, wing: Wing, i: number): number {
  if (store === null || wing.slot[i] < 0) return -1
  const e = wing.eid[i]
  const s = Agent.index[e]
  return s >= 0 && s < store.count && store.eid[s] === e ? s : -1
}

/**
 * One wing step (per sim tick). Allocation-free on a quiet tick. `flownDefId`: the ship def id the player flies
 * (`CareerPort.flownShipDefId`), null to read the player's def slot id. `sites`: the live layout's stations (a recruit's
 * home radius, `homeRadiusAt`); null: every home is a point.
 */
export function stepWing(rt: WingRuntime, sim: Sim, store: ContactStore | null, flownDefId: string | null = null, sites: HomeSites | null = null): void {
  const wing = rt.wing
  if (rt.sim !== sim) {
    rt.sim = sim
    rt.profiles.clear()
    const list = sim.data.aiProfiles.profiles
    for (let i = 0; i < list.length; i++) rt.profiles.set(list[i].id, i)
    setPilotOverlay(createWingOverlay(wing, sim.data.aiProfiles))
    rt.leaderDef = -1
  }
  // Leader, cap and effectiveness.
  const p = sim.playerEid
  const leader = p >= 0 && Life.alive[p] === 1 ? p : -1
  const def = leader >= 0 ? Ship.defIndex[leader] : -1
  if (leader !== wing.leader || def !== rt.leaderDef || flownDefId !== rt.leaderDefId) {
    if (leader !== wing.leader) rt.leaderAt = sim.time
    rt.leaderDef = def
    rt.leaderDefId = flownDefId
    const defId = def >= 0 ? (flownDefId ?? sim.defs[def].id) : null
    let over = setLeader(wing, leader, defId === null ? null : classOfShipDef(defId), defId === null ? null : wingSizeOfShipDef(defId))
    while (over-- > 0) handBack(rt, sim, store, wing.count - 1, false)
  }
  if (wing.recall) {
    wing.recall = false
    rt.returned.clear()
  }
  if (store !== null) {
    if (leader >= 0 && (rt.followers.size > 0 || wing.count < wing.cap)) placeWaiting(rt, store, leader, sim.time - rt.leaderAt >= wing.cfg.tuning.launchS)
    for (let i = wing.count - 1; i >= 0; i--) {
      if (wing.slot[i] < 0) continue
      const s = slotOf(store, wing, i)
      if (s < 0 || !hasComponent(sim.world, wing.eid[i], AIPilot)) {
        // Demoted or removed by the traffic layer: the ship is gone (despawning leaves `Life.alive` set) and its
        // entity id may already fly another ship, so the roster only forgets it.
        dropMember(wing, i)
      } else if (store.own[s] !== 1 || store.fleet[s]?.escortPlayer !== true) {
        // No longer Escort(the player): back to the traffic pilot.
        handBack(rt, sim, store, i, false)
      } else {
        wing.slot[i] = s
        store.combatAt[s] = sim.time
        if (store.kind[s] === STEP_KIND.leg && store.purpose[s] !== PURPOSE.exit) {
          // The core's next step (after a placed dock or hold) is a leg to the escort post; the wing flies the ship,
          // so it never arrives. A hold on the leg's clock reports at its end, and the post stays the Dock/return home.
          store.kind[s] = STEP_KIND.holding
          store.purpose[s] = 0
        }
      }
    }
    if (leader >= 0 && wing.count < wing.cap) recruit(rt, sim, store, sites)
  }
  for (let i = wing.count - 1; i >= 0; i--) if (wing.docked[i] === 1) handBack(rt, sim, store, i, true)
  updateWing(wing, sim)
  // The relay follow reads the last tick's distances (the traffic subsystem despawns the ships first on leaving).
  const r2 = wing.cfg.followRelayM * wing.cfg.followRelayM
  rt.near.fill(0)
  if (leader >= 0) {
    for (let i = 0; i < wing.count; i++) if (distSq(wing.eid[i], leader) <= r2) rt.near[i] = 1
  }
}

/** Squared distance between two entities, float64. */
function distSq(a: number, b: number): number {
  const dx = Pos64.x[a] - Pos64.x[b]
  const dy = Pos64.y[a] - Pos64.y[b]
  const dz = Pos64.z[a] - Pos64.z[b]
  return dx * dx + dy * dy + dz * dz
}

/** Recruits waiting Escort(the player) ships up to the cap. */
function recruit(rt: WingRuntime, sim: Sim, store: ContactStore, sites: HomeSites | null): void {
  const wing = rt.wing
  for (let s = 0; s < store.count && wing.count < wing.cap; s++) {
    const eid = store.eid[s]
    if (eid < 0 || store.own[s] !== 1 || Life.alive[eid] !== 1 || isWingman(wing, eid)) continue
    const fleet = store.fleet[s]
    if (fleet === null || fleet.escortPlayer !== true || rt.returned.has(fleet.shipId)) continue
    const profile = rt.profiles.get(combatProfileId(wing.cfg, fleet.skills)) ?? 0
    const hr = homeRadiusAt(sites, store.tx[s], store.ty[s], store.tz[s])
    if (recruitWingman(wing, sim, eid, s, profile, store.tx[s], store.ty[s], store.tz[s], hr)) store.combatAt[s] = sim.time
  }
}

/** Releases member `i` back to the traffic pilot (its step); `home` marks it sent home. */
function handBack(rt: WingRuntime, sim: Sim, store: ContactStore | null, i: number, home: boolean): void {
  const wing = rt.wing
  const eid = wing.eid[i]
  const s = slotOf(store, wing, i)
  releaseWingman(wing, sim, i)
  if (store === null || s < 0) return
  if (home) {
    const fleet = store.fleet[s]
    if (fleet !== null) rt.returned.add(fleet.shipId)
  }
  if (Life.alive[eid] === 1) beginTrafficPilot(pilotEnvFor(rt, store), eid, s)
}

/**
 * Places waiting contacts at the player's formation slots so the tier step promotes them beside the player: the relay
 * followers the core now lists, and (up to the cap) every other Escort(the player) contact in the system that is not
 * docked and not sent home. The strategic escort only knows the player's system, not where the player is, and parks
 * at its post there (a relay, light-seconds out), where the tier step would never promote it (D-418). Allocation-free.
 */
function placeWaiting(rt: WingRuntime, store: ContactStore, leader: number, launched: boolean): void {
  const wing = rt.wing
  let k = wing.count
  for (let s = 0; s < store.count && k < wing.max; s++) {
    if (store.own[s] !== 1 || store.eid[s] >= 0) continue
    const fleet = store.fleet[s]
    if (fleet === null) continue
    if (rt.followers.has(fleet.shipId)) rt.followers.delete(fleet.shipId)
    else if (fleet.escortPlayer !== true || k >= wing.cap || !waiting(store, s, launched) || rt.returned.has(fleet.shipId)) continue
    writeSlotWorld(wing.formation, k, wing.cfg.tuning.formation, leader, rt.place)
    const x = rt.place[k * 3]
    const y = rt.place[k * 3 + 1]
    const z = rt.place[k * 3 + 2]
    store.x[s] = x
    store.y[s] = y
    store.z[s] = z
    if (store.kind[s] === STEP_KIND.docked) {
      // Out of the dock (D-459 bug 8): a hold here on the docked step's clock (it reports at `t1`); its post (`tx`,
      // the station) stays the Dock/return home. The core steps the escort order when the dock ends.
      store.kind[s] = STEP_KIND.holding
      store.purpose[s] = 0
    } else if (store.kind[s] === STEP_KIND.leg && store.purpose[s] !== PURPOSE.exit) {
      // A strategic leg crosses the system in seconds (a restarted leg is past `cruiseClearM` a tick later, before the
      // tier step runs), so the ship holds here instead: its post (`tx`) and the leg's clock (it reports at `t1`) kept.
      store.kind[s] = STEP_KIND.holding
      store.purpose[s] = 0
    } else if (store.kind[s] === STEP_KIND.leg) {
      // The leg restarts here, so the tier step sees it just clear of its start and promotes it.
      store.fx[s] = x
      store.fy[s] = y
      store.fz[s] = z
      store.t0[s] = store.time[0]
    }
    k++
  }
}

/**
 * True when contact `s` may be brought to the player: not on its way out of the system, and when docked only once
 * the player has been in space `launchS` (`launched`).
 */
function waiting(store: ContactStore, s: number, launched: boolean): boolean {
  const kind = store.kind[s]
  if (kind === STEP_KIND.docked) return launched
  return !(kind === STEP_KIND.leg && store.purpose[s] === PURPOSE.exit)
}

/** The system is being left: remember the members near the player, then forget the roster (the ships are gone). */
export function leaveWing(rt: WingRuntime, sim: Sim, store: ContactStore | null): void {
  const wing = rt.wing
  rt.followers.clear()
  for (let i = 0; i < wing.count; i++) {
    if (rt.near[i] !== 1 || store === null || wing.slot[i] < 0 || wing.slot[i] >= store.count) continue
    const fleet = store.fleet[wing.slot[i]]
    if (fleet !== null) rt.followers.add(fleet.shipId)
  }
  for (let i = wing.count - 1; i >= 0; i--) {
    if (Life.alive[wing.eid[i]] === 1) releaseWingman(wing, sim, i)
    else dropMember(wing, i)
  }
  rt.near.fill(0)
}

// -------------------------------------------------------------------------------------------------
// The command API (P31 radial menu, P35 alerts, P36)
// -------------------------------------------------------------------------------------------------

let active: WingRuntime | null = null

/** The live wing (null before the Reach runs). */
export function activeWing(): Wing | null {
  return active?.wing ?? null
}

/** Commands the live wing (see `issueWingCommand`); false when there is no wing yet. */
export function commandWing(cmd: WingCommandName, formation?: FormationName): boolean {
  if (active === null) return false
  issueWingCommand(active.wing, cmd, formation)
  return true
}

/** Fleet ship ids that will arrive with the player in the next system (the relay follow). */
export function wingRelayFollowers(): ReadonlySet<string> {
  return active?.followers ?? new Set()
}

export default function createWingSubsystem(): ReachSubsystem {
  const rt = createWingRuntime()
  active = rt
  return {
    name: 'wing',
    order: 455,
    init(): void {},
    step(core, sim): void {
      stepWing(rt, sim, activeTrafficStore(), core.career.flownShipDefId?.() ?? null, core.session.layout?.stations ?? null)
    },
    onSystemLeave(_core, sim): void {
      leaveWing(rt, sim, activeTrafficStore())
    },
  }
}
